import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motionActions } from './store/motion';
import useFeatures from './common/util/useFeatures';

const DEFAULT_POLL_MS = 15000;
const MIN_POLL_MS = 5000;
const MAX_POLL_MS = 120000;
const MAX_CONSECUTIVE_ERRORS = 5;

const MotionController = () => {
  const dispatch = useDispatch();
  const features = useFeatures();

  const authenticated = useSelector((state) => Boolean(state.session.user));
  const userAttributes = useSelector((state) => state.session.user?.attributes || {});
  const serverAttributes = useSelector((state) => state.session.server?.attributes || {});

  const errorCountRef = useRef(0);

  useEffect(() => {
    if (!authenticated || !features.enableMotionController) {
      dispatch(motionActions.setEnabled(false));
      return undefined;
    }

    const configuredPollMs = Number(
      userAttributes['ui.motionPollMs'] || serverAttributes['ui.motionPollMs'] || DEFAULT_POLL_MS,
    );
    const pollMs = Math.max(MIN_POLL_MS, Math.min(MAX_POLL_MS, configuredPollMs));
    const endpoint = userAttributes['ui.motionEndpoint'] || serverAttributes['ui.motionEndpoint'] || '/api/motion';

    let cancelled = false;

    const poll = async () => {
      dispatch(motionActions.requestStart());
      try {
        const response = await fetch(endpoint);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          dispatch(motionActions.requestSuccess(Array.isArray(data) ? data : []));
          dispatch(motionActions.setEnabled(true));
          errorCountRef.current = 0;
        }
      } catch (error) {
        errorCountRef.current += 1;
        if (!cancelled) {
          dispatch(motionActions.requestFailure(error?.message || 'network_error'));
          if (errorCountRef.current >= MAX_CONSECUTIVE_ERRORS) {
            dispatch(motionActions.setEnabled(false));
          }
        }
      }
    };

    poll();
    const timer = setInterval(poll, pollMs);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [authenticated, features.enableMotionController, userAttributes, serverAttributes, dispatch]);

  return null;
};

export default MotionController;
